import { useState } from 'react';
import imageCompression from 'browser-image-compression';
import { CLOUDINARY_CONFIG } from '../config/cloudinary';

export interface CloudinaryImage {
  url: string;
  thumbnail: string;
  public_id: string;
}

export const useCloudinaryUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const compressImage = async (file: File): Promise<File> => {
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      return file;
    }

    const fileSizeMB = file.size / 1024 / 1024;

    // Only compress if larger than 2MB
    if (fileSizeMB < 2) {
      console.log(
        `File size (${fileSizeMB.toFixed(
          2
        )}MB) is under 2MB, skipping compression`
      );
      return file;
    }

    console.log(`Compressing image: ${file.name} (${fileSizeMB.toFixed(2)}MB)`);

    const options = {
      maxSizeMB: 2, // Target max size: 2MB
      maxWidthOrHeight: 1920, // Max dimension (maintains quality)
      useWebWorker: true, // Better performance
      fileType: 'image/jpeg', // Convert to JPEG for better compression
    };

    try {
      const compressedFile = await imageCompression(file, options);
      const compressedSizeMB = compressedFile.size / 1024 / 1024;
      console.log(`✅ Compressed to ${compressedSizeMB.toFixed(2)}MB`);
      return compressedFile;
    } catch (error) {
      console.error('⚠️ Compression failed, using original:', error);
      return file; // Fallback to original if compression fails
    }
  };

  const uploadImage = async (
    file: File,
    folder: string = 'neev'
  ): Promise<CloudinaryImage> => {
    setUploading(true);
    setProgress(0);

    try {
      // Compress large images before upload
      const fileToUpload = await compressImage(file);

      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('upload_preset', CLOUDINARY_CONFIG.uploadPreset);
      formData.append('folder', folder);

      const response = await fetch(CLOUDINARY_CONFIG.uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Upload failed');
      }

      const data = await response.json();
      setProgress(100);
      setUploading(false);

      // Generate thumbnail URL (300x200, cropped)
      const thumbnailUrl = data.secure_url.replace(
        '/upload/',
        '/upload/w_300,h_200,c_fill,q_auto/'
      );

      return {
        url: data.secure_url,
        thumbnail: thumbnailUrl,
        public_id: data.public_id,
      };
    } catch (error) {
      setUploading(false);
      setProgress(0);
      console.error('Cloudinary upload error:', error);
      throw error;
    }
  };

  const uploadMultipleImages = async (
    files: File[],
    folder: string = 'neev'
  ): Promise<CloudinaryImage[]> => {
    setUploading(true);
    const uploadPromises = files.map((file) => uploadImage(file, folder));

    try {
      const results = await Promise.all(uploadPromises);
      setUploading(false);
      return results;
    } catch (error) {
      setUploading(false);
      throw error;
    }
  };

  return {
    uploadImage,
    uploadMultipleImages,
    uploading,
    progress,
  };
};
